import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PointerEvent as ReactPointerEvent,
    type RefObject,
} from "react";

/** Multiplicative zoom factor per mouse-wheel tick. */
export const ZOOM_STEP = 1.0015;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 20;
/** Per-click zoom factor for the on-screen − / + buttons. */
const BUTTON_ZOOM_FACTOR = 1.2;

export interface ViewBox {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** Convert a client-space (screen) point into SVG user-space, accounting for
 * the element's current viewBox/zoom/pan. */
export function clientToSvgPoint(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    return { x: pt.x, y: pt.y };
}

export interface UseSvgViewBoxZoomOptions {
    /** The "home"/reset viewBox. NOT assumed to be at origin 0,0. */
    base: ViewBox;
    minZoom?: number;
    maxZoom?: number;
    /** Wheel zoom step (multiplicative per deltaY unit). */
    zoomStep?: number;
}

export interface SvgViewBoxPanHandlers {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: (e: ReactPointerEvent) => void;
    onPointerCancel: (e: ReactPointerEvent) => void;
}

export interface UseSvgViewBoxZoomResult {
    /** Attach to the target <svg>. The hook wires its own non-passive wheel
     * listener to this element. */
    svgRef: RefObject<SVGSVGElement>;
    /** `${x} ${y} ${w} ${h}` — pass to the <svg>'s viewBox attribute. */
    viewBoxAttr: string;
    view: ViewBox;
    /** Middle-mouse-drag pan handlers. Spread onto the <svg> or compose with
     * existing pointer handlers (these only act on button 1 / active pans). */
    panHandlers: SvgViewBoxPanHandlers;
    /** Integer zoom percentage relative to the base frame (100 = un-zoomed). */
    zoomPercent: number;
    /** Button zoom in/out, anchored on the viewport center. */
    zoomIn: () => void;
    zoomOut: () => void;
    /** Restore the base/home frame. */
    resetView: () => void;
}

/**
 * Shared SVG zoom/pan driven by mutating the viewBox: cursor-anchored wheel
 * zoom plus middle-mouse-drag pan, matching the 2D SVG diagram's UX. The
 * on-screen − / + / ⌂ buttons drive the same view (zoomed about the center).
 */
export function useSvgViewBoxZoom({
    base,
    minZoom = MIN_ZOOM,
    maxZoom = MAX_ZOOM,
    zoomStep = ZOOM_STEP,
}: UseSvgViewBoxZoomOptions): UseSvgViewBoxZoomResult {
    const svgRef = useRef<SVGSVGElement>(null);

    const [view, setView] = useState<ViewBox>(base);
    const viewRef = useRef(view);
    viewRef.current = view;

    // Re-frame to the new base when the document dimensions/origin change, but
    // only while the user is still on the default frame (hasn't zoomed/panned).
    const baseRef = useRef(base);
    useEffect(() => {
        const prev = baseRef.current;
        baseRef.current = base;
        setView((cur) => {
            const onDefaultFrame =
                cur.x === prev.x &&
                cur.y === prev.y &&
                cur.w === prev.w &&
                cur.h === prev.h;
            return onDefaultFrame ? base : cur;
        });
    }, [base.x, base.y, base.w, base.h]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Zoom about a fixed SVG-space anchor, keeping that point under the cursor. */
    const zoomToAnchor = useCallback(
        (anchorX: number, anchorY: number, factor: number) => {
            setView((cur) => {
                const baseScale = baseRef.current.w / cur.w; // current zoom
                const nextScale = Math.min(
                    maxZoom,
                    Math.max(minZoom, baseScale / factor),
                );
                const w = baseRef.current.w / nextScale;
                const h = baseRef.current.h / nextScale;
                const fx = (anchorX - cur.x) / cur.w;
                const fy = (anchorY - cur.y) / cur.h;
                return { x: anchorX - fx * w, y: anchorY - fy * h, w, h };
            });
        },
        [minZoom, maxZoom],
    );

    // Cursor-anchored wheel zoom. Registered as a native non-passive listener so
    // preventDefault() actually stops page scroll (React attaches onWheel
    // passively at the root).
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const cursor = clientToSvgPoint(svg, e.clientX, e.clientY);
            zoomToAnchor(cursor.x, cursor.y, Math.pow(zoomStep, e.deltaY));
        };
        svg.addEventListener("wheel", onWheel, { passive: false });
        return () => svg.removeEventListener("wheel", onWheel);
    }, [zoomStep, zoomToAnchor]);

    /** Zoom about the current viewport center (for the − / + buttons). */
    const zoomByButton = useCallback(
        (factor: number) => {
            const cur = viewRef.current;
            zoomToAnchor(cur.x + cur.w / 2, cur.y + cur.h / 2, factor);
        },
        [zoomToAnchor],
    );

    const zoomIn = useCallback(
        () => zoomByButton(BUTTON_ZOOM_FACTOR),
        [zoomByButton],
    );
    const zoomOut = useCallback(
        () => zoomByButton(1 / BUTTON_ZOOM_FACTOR),
        [zoomByButton],
    );

    const resetView = useCallback(() => setView(baseRef.current), []);

    // Middle-button pan: start point in client coords + view at press time.
    const panRef = useRef<{
        startX: number;
        startY: number;
        view: ViewBox;
    } | null>(null);

    const onPointerDown = useCallback((e: ReactPointerEvent) => {
        if (e.button !== 1) return;
        e.preventDefault();
        panRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            view: viewRef.current,
        };
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
    }, []);

    const onPointerMove = useCallback((e: ReactPointerEvent) => {
        const pan = panRef.current;
        if (!pan) return;
        const rect = (e.currentTarget as Element).getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const scaleX = pan.view.w / rect.width;
        const scaleY = pan.view.h / rect.height;
        const dx = (e.clientX - pan.startX) * scaleX;
        const dy = (e.clientY - pan.startY) * scaleY;
        setView({
            x: pan.view.x - dx,
            y: pan.view.y - dy,
            w: pan.view.w,
            h: pan.view.h,
        });
    }, []);

    const endPan = useCallback((e: ReactPointerEvent) => {
        if (!panRef.current) return;
        panRef.current = null;
        const el = e.currentTarget as Element;
        if (el.hasPointerCapture?.(e.pointerId)) {
            el.releasePointerCapture(e.pointerId);
        }
    }, []);

    const panHandlers = useMemo<SvgViewBoxPanHandlers>(
        () => ({
            onPointerDown,
            onPointerMove,
            onPointerUp: endPan,
            onPointerCancel: endPan,
        }),
        [onPointerDown, onPointerMove, endPan],
    );

    const viewBoxAttr = `${view.x} ${view.y} ${view.w} ${view.h}`;
    const zoomPercent = Math.round((baseRef.current.w / view.w) * 100);

    return {
        svgRef,
        viewBoxAttr,
        view,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    };
}
